import Axios, { AxiosInstance, AxiosResponse } from 'axios';
import Cookies from 'universal-cookie';
import { User } from '../types/user';
import { SearchResponseInterface } from '../components/context/contextInterfaces';
import { AnyObject } from '../types/anyObject';

const cookies = new Cookies();

/**
 *
 * @param {Object} error
 * @throws It will throw the new error based on the try-catch encapsolution on every request
 */
// const errorHandler = (error) => {
//   throw new Error(`Following error has been thrown
//      ${error}`);
// };

/**
 *
 * @param {String} email
 * @param {String} password
 * @returns {Promise<T> | Object} Returned object is user data
 * @method
 */
export const Login = async (email: string, password: string): Promise<User> => {
  const encodedCredentials = btoa(`${email}:${password}`);
  const loginData = { email, password };
  const loginUrl = `${process.env.REACT_APP_URL}login`;
  return new Promise<User | any>((resolve, reject) => {
    Axios.post<User>(loginUrl, loginData, {
      headers: {
        Authorization: `Basic ${encodedCredentials}`,
        contentType: 'application/json',
      },
    })
      .then(resolve)
      .catch((error) => {
        const response = error.response ? error.response : undefined;
        return reject(response);
      });
  });
};

/**
 *
 * @returns {AxiosInstance}
 * @method
 */
const CreateHeaderWithBearerAuthentication = (): AxiosInstance =>
  Axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
    headers: {
      Authorization: `Bearer ${cookies.get('userToken')}`,
      contentType: 'application/json',
    },
  });

/**
 *
 * @param {String} item
 * @param {Number} page
 * @param {Number} size
 * @param {Number} province_code
 * @returns {Promise<any> | Array} It will return array of objects where the array is related to the requested item
 * @method
 */
export const GetAll = async <T = any>(
  item: string,
  page: number,
  size: number,
  province_code?: number,
  config: any = null,
): Promise<AxiosResponse<T[]>> => {
  const provinceCode = `&province_code=${province_code}`;

  if (page === -1 || !page) {
    return CreateHeaderWithBearerAuthentication().get<T[]>(`${item}`);
  }
  if (config) {
    return CreateHeaderWithBearerAuthentication().get<T[]>(
      `${item}?page=${page}&size=${size}${province_code ? provinceCode : ''}`,
      config,
    );
  }
  return CreateHeaderWithBearerAuthentication().get<T[]>(
    `${item}?page=${page}&size=${size}${province_code ? provinceCode : ''}`,
  );
};

export const GetItem = async <T = any>(item: string): Promise<AxiosResponse<T>> =>
  CreateHeaderWithBearerAuthentication().get(`${item}`);
// .catch((err) => {
//   if (err.response.status === 401) {
//     CreateHeaderWithBearerAuthentication()
//       .post('http://bitweb-test.com/beauty/public/api/v1/refresh')
//       .then((res) => {
//         console.log(res);
//       })
//       .catch((error) => {
//         const response = error.response ? error.response : undefined;
//         return Promise.reject(response);
//       });
//   }
//   return Promise.reject(err);
// });

/**
 *
 * @param {String} item
 * @param {Number} id
 * @returns {Promise<any> | Object} Returned object is based on requested item and id
 * @method
 */
export const Get = async <T = any>(item: string, id: number | string): Promise<AxiosResponse<T>> =>
  CreateHeaderWithBearerAuthentication().get(`${item}/${id}`);

/**
 *
 * @param {String} item
 * @param {Number} id
 * @returns {Promise<AxiosResponse<T>>}
 * @method
 */
export const Delete = (item: string, id: string | number): Promise<AxiosResponse> =>
  CreateHeaderWithBearerAuthentication().delete(`${item}/${id}`);

/**
 *
 * @param {String} item
 * @param {Object} object
 * @returns {Promise<T> | Object} Returned object is based on server response
 * @method
 */
export const Post = async <T = any>(item: string, object: T): Promise<AxiosResponse<T>> =>
  CreateHeaderWithBearerAuthentication().post(`${item}`, object);

export const PostRefresh = async <T = any>(): Promise<AxiosResponse<T>> =>
  CreateHeaderWithBearerAuthentication().post(`${process.env.REACT_APP_URL}refresh`);

/**
 *
 * @param {String} item
 * @param id
 * @param {Object} object
 * @returns {Promise<T> | Object} Returned object is based on server response
 * @method
 */

export const Put = async <T = any>(item: string, id: string | number, object: T): Promise<AxiosResponse<T>> =>
  CreateHeaderWithBearerAuthentication().put([item, id].join('/'), object);

export const Search = async <T = any>(item: string, object: AnyObject): Promise<AxiosResponse<T>> =>
  CreateHeaderWithBearerAuthentication().post(`${item}/search`, object);

export const ListReport = async (item: string, object: AnyObject): Promise<AxiosResponse> =>
  CreateHeaderWithBearerAuthentication().post(`${item}/listReport`, object);

export const FetchHistory = async <T = any>(
  item: string,
  object: string | number,
): Promise<AxiosResponse<SearchResponseInterface<T>>> =>
  CreateHeaderWithBearerAuthentication().get(`histories/${object}`);

export const AutoComplete = async <T = any>(item: string, object: AnyObject): Promise<AxiosResponse<T>> =>
  CreateHeaderWithBearerAuthentication().post(`${item}/options`, object);

export const FetchImage = async (item: string, id: string | number, path: string): Promise<AxiosResponse> =>
  CreateHeaderWithBearerAuthentication().get(`${item}/${id}/${path}`, {
    responseType: 'blob',
  });
